/**
 * Copyright (c) 2018-2022 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign, __extends } from "tslib";
import { StateObject, StateTransformer } from '../mol-state';
export var PluginStateObject;
(function (PluginStateObject) {
    PluginStateObject.Create = StateObject.factory();
    function isRepresentation3D(o) {
        return !!o && o.type.typeClass === 'Representation3D';
    }
    PluginStateObject.isRepresentation3D = isRepresentation3D;
    function isBehavior(o) {
        return !!o && o.type.typeClass === 'Behavior';
    }
    PluginStateObject.isBehavior = isBehavior;
    function CreateRepresentation3D(type) {
        return PluginStateObject.Create(__assign(__assign({}, type), { typeClass: 'Representation3D' }));
    }
    PluginStateObject.CreateRepresentation3D = CreateRepresentation3D;
    function CreateBehavior(type) {
        return PluginStateObject.Create(__assign(__assign({}, type), { typeClass: 'Behavior' }));
    }
    PluginStateObject.CreateBehavior = CreateBehavior;
    var Root = /** @class */ (function (_super) {
        __extends(Root, _super);
        function Root() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        return Root;
    }(PluginStateObject.Create({ name: 'Root', typeClass: 'Root' })));
    PluginStateObject.Root = Root;
    var Group = /** @class */ (function (_super) {
        __extends(Group, _super);
        function Group() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        return Group;
    }(PluginStateObject.Create({ name: 'Group', typeClass: 'Group' })));
    PluginStateObject.Group = Group;
    var Data;
    (function (Data) {
        var String = /** @class */ (function (_super) {
            __extends(String, _super);
            function String() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return String;
        }(PluginStateObject.Create({ name: 'String Data', typeClass: 'Data', })));
        Data.String = String;
        var Binary = /** @class */ (function (_super) {
            __extends(Binary, _super);
            function Binary() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Binary;
        }(PluginStateObject.Create({ name: 'Binary Data', typeClass: 'Data' })));
        Data.Binary = Binary;
        var Blob = /** @class */ (function (_super) {
            __extends(Blob, _super);
            function Blob() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Blob;
        }(PluginStateObject.Create({ name: 'Data Blob', typeClass: 'Data' })));
        Data.Blob = Blob;
    })(Data = PluginStateObject.Data || (PluginStateObject.Data = {}));
    var Format;
    (function (Format) {
        var Json = /** @class */ (function (_super) {
            __extends(Json, _super);
            function Json() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Json;
        }(PluginStateObject.Create({ name: 'JSON Data', typeClass: 'Data' })));
        Format.Json = Json;
        var Cif = /** @class */ (function (_super) {
            __extends(Cif, _super);
            function Cif() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Cif;
        }(PluginStateObject.Create({ name: 'CIF File', typeClass: 'Data' })));
        Format.Cif = Cif;
        var Cube = /** @class */ (function (_super) {
            __extends(Cube, _super);
            function Cube() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Cube;
        }(PluginStateObject.Create({ name: 'Cube File', typeClass: 'Data' })));
        Format.Cube = Cube;
        var Psf = /** @class */ (function (_super) {
            __extends(Psf, _super);
            function Psf() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Psf;
        }(PluginStateObject.Create({ name: 'PSF File', typeClass: 'Data' })));
        Format.Psf = Psf;
        var Prmtop = /** @class */ (function (_super) {
            __extends(Prmtop, _super);
            function Prmtop() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Prmtop;
        }(PluginStateObject.Create({ name: 'PRMTOP File', typeClass: 'Data' })));
        Format.Prmtop = Prmtop;
        var Top = /** @class */ (function (_super) {
            __extends(Top, _super);
            function Top() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Top;
        }(PluginStateObject.Create({ name: 'TOP File', typeClass: 'Data' })));
        Format.Top = Top;
        var Ply = /** @class */ (function (_super) {
            __extends(Ply, _super);
            function Ply() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Ply;
        }(PluginStateObject.Create({ name: 'PLY File', typeClass: 'Data' })));
        Format.Ply = Ply;
        var Ccp4 = /** @class */ (function (_super) {
            __extends(Ccp4, _super);
            function Ccp4() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Ccp4;
        }(PluginStateObject.Create({ name: 'CCP4/MRC/MAP File', typeClass: 'Data' })));
        Format.Ccp4 = Ccp4;
        var Dsn6 = /** @class */ (function (_super) {
            __extends(Dsn6, _super);
            function Dsn6() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Dsn6;
        }(PluginStateObject.Create({ name: 'DSN6/BRIX File', typeClass: 'Data' })));
        Format.Dsn6 = Dsn6;
        var Dx = /** @class */ (function (_super) {
            __extends(Dx, _super);
            function Dx() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Dx;
        }(PluginStateObject.Create({ name: 'DX File', typeClass: 'Data' })));
        Format.Dx = Dx;
        var Blob = /** @class */ (function (_super) {
            __extends(Blob, _super);
            function Blob() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Blob;
        }(PluginStateObject.Create({ name: 'Format Blob', typeClass: 'Data' })));
        Format.Blob = Blob;
    })(Format = PluginStateObject.Format || (PluginStateObject.Format = {}));
    var Molecule;
    (function (Molecule) {
        var Coordinates = /** @class */ (function (_super) {
            __extends(Coordinates, _super);
            function Coordinates() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Coordinates;
        }(PluginStateObject.Create({ name: 'Coordinates', typeClass: 'Object' })));
        Molecule.Coordinates = Coordinates;
        var Topology = /** @class */ (function (_super) {
            __extends(Topology, _super);
            function Topology() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Topology;
        }(PluginStateObject.Create({ name: 'Topology', typeClass: 'Object' })));
        Molecule.Topology = Topology;
        var Model = /** @class */ (function (_super) {
            __extends(Model, _super);
            function Model() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Model;
        }(PluginStateObject.Create({ name: 'Model', typeClass: 'Object' })));
        Molecule.Model = Model;
        var Trajectory = /** @class */ (function (_super) {
            __extends(Trajectory, _super);
            function Trajectory() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Trajectory;
        }(PluginStateObject.Create({ name: 'Trajectory', typeClass: 'Object' })));
        Molecule.Trajectory = Trajectory;
        var Structure = /** @class */ (function (_super) {
            __extends(Structure, _super);
            function Structure() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Structure;
        }(PluginStateObject.Create({ name: 'Structure', typeClass: 'Object' })));
        Molecule.Structure = Structure;
        (function (Structure) {
            var Representation3D = /** @class */ (function (_super) {
                __extends(Representation3D, _super);
                function Representation3D() {
                    return _super !== null && _super.apply(this, arguments) || this;
                }
                return Representation3D;
            }(CreateRepresentation3D({ name: 'Structure 3D' })));
            Structure.Representation3D = Representation3D;
            var Representation3DState = /** @class */ (function (_super) {
                __extends(Representation3DState, _super);
                function Representation3DState() {
                    return _super !== null && _super.apply(this, arguments) || this;
                }
                return Representation3DState;
            }(PluginStateObject.Create({ name: 'Structure 3D State', typeClass: 'Object' })));
            Structure.Representation3DState = Representation3DState;
            var Selections = /** @class */ (function (_super) {
                __extends(Selections, _super);
                function Selections() {
                    return _super !== null && _super.apply(this, arguments) || this;
                }
                return Selections;
            }(PluginStateObject.Create({ name: 'Selections', typeClass: 'Object' })));
            Structure.Selections = Selections;
        })(Structure = Molecule.Structure || (Molecule.Structure = {}));
    })(Molecule = PluginStateObject.Molecule || (PluginStateObject.Molecule = {}));
    var Volume;
    (function (Volume) {
        var Data = /** @class */ (function (_super) {
            __extends(Data, _super);
            function Data() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Data;
        }(PluginStateObject.Create({ name: 'Volume', typeClass: 'Object' })));
        Volume.Data = Data;
        var Lazy = /** @class */ (function (_super) {
            __extends(Lazy, _super);
            function Lazy() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Lazy;
        }(PluginStateObject.Create({ name: 'Lazy Volume', typeClass: 'Object' })));
        Volume.Lazy = Lazy;
        var Representation3D = /** @class */ (function (_super) {
            __extends(Representation3D, _super);
            function Representation3D() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Representation3D;
        }(CreateRepresentation3D({ name: 'Volume 3D' })));
        Volume.Representation3D = Representation3D;
    })(Volume = PluginStateObject.Volume || (PluginStateObject.Volume = {}));
    var Shape;
    (function (Shape) {
        var Provider = /** @class */ (function (_super) {
            __extends(Provider, _super);
            function Provider() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Provider;
        }(PluginStateObject.Create({ name: 'Shape Provider', typeClass: 'Object' })));
        Shape.Provider = Provider;
        var Representation3D = /** @class */ (function (_super) {
            __extends(Representation3D, _super);
            function Representation3D() {
                return _super !== null && _super.apply(this, arguments) || this;
            }
            return Representation3D;
        }(CreateRepresentation3D({ name: 'Shape 3D' })));
        Shape.Representation3D = Representation3D;
    })(Shape = PluginStateObject.Shape || (PluginStateObject.Shape = {}));
})(PluginStateObject || (PluginStateObject = {}));
export var PluginStateTransform;
(function (PluginStateTransform) {
    PluginStateTransform.CreateBuiltIn = StateTransformer.factory('ms-plugin');
    PluginStateTransform.BuiltIn = StateTransformer.builderFactory('ms-plugin');
})(PluginStateTransform || (PluginStateTransform = {}));
